/**
 * possible statuses
 *
 * https://bitbucket.hotterpads.com/projects/RP/repos/rpjava/browse/app/rent-payment/server/src/main/java/com/hotpads/rentpayment/model/managetenants/PaymentCardStatus.java
 *
 * possible payment descriptions
 *
 * https://bitbucket.hotterpads.com/projects/RP/repos/rpjava/browse/app/rent-payment/server/src/main/java/com/hotpads/rentpayment/model/managetenants/PaymentCardText.java
 *
 * possible CTA
 *
 * https://bitbucket.hotterpads.com/projects/RP/repos/rpjava/browse/app/rent-payment/server/src/main/java/com/hotpads/rentpayment/model/managetenants/CallToActionText.java
 */

interface PaymentError {
  code: string;
  code_details?: string;
  message?: string;
}

/**
 * Error code constants - note that ZE-UI is a general error code
 * and the actual error type is determined by the code_details field
 */
const ERROR_CODES = {
  ZE_UI: 'ZE-UI',
  METROID_MAINTENANCE_MODE: 'METROID_MAINTENANCE_MODE',
  CANNOT_MAKE_PAYMENTS: 'CANNOT_MAKE_PAYMENTS',
};

/**
 * Regex patterns for parsing code_details field
 * Based on Metroid documentation: https://confluence.zgtools.net/x/2pxQHw
 *
 * ZE-UI code_details follow the format: [failure_reason] failure_message
 * where failure_reason is one of:
 * - GatewayTransactionError (most common)
 * - Submit_Too_Quick
 * - HostedPageFieldValidationError
 * - Invalid_Values
 * - Invalid_TimeOut
 * - Attempt_Exceed_Limitation
 * - ReCaptcha_Validation_Failed
 */
const ERROR_REGEX = {
  // Form validation errors
  EXPIRATION_PAST: /Expiration date must be a future date/i,
  ZIP_CODE: /ZIP code/i,
  INVALID_CARD_NUMBER: /Invalid CC Number/i,

  // ZE-UI failure reasons (bracketed format from Metroid docs)
  GATEWAY_TRANSACTION_ERROR: /\[GatewayTransactionError\]/i,
  ATTEMPTS_EXCEEDED: /\[?Attempt_Exceed_Limitation\]?/i,
  SUBMIT_TOO_QUICK: /\[?Submit_Too_Quick\]?/i,
  HOSTED_PAGE_FIELD_VALIDATION_ERROR: /\[HostedPageFieldValidationError\]/i,
  INVALID_VALUES: /\[Invalid_Values\]/i,
  INVALID_TIMEOUT: /\[Invalid_TimeOut\]/i,
  RECAPTCHA_VALIDATION_FAILED: /\[ReCaptcha_Validation_Failed\]/i,

  // Additional error patterns (backward compatibility with older formats)
  TRANSACTION_DECLINED: /Transaction declined/i,
  CALL_VOICE_CENTER: /Call Voice Center/i,
  CUSTOMER_NOT_AUTHORIZED: /Customer Advises Not Authorized/i,
  DOWNSTREAM: /Downstream/i,
};
const PAYMENT_TYPE = {
  FAST_PAYOUT: 'fast payout',
  ONE_TIME_PAYMENT: 'one time payment',
  RECURRING_PAYMENT: 'recurring payment',
};

const PAYMENT_STATUS_TEXT = {
  FAILED: 'failed',
  PAID: 'paid',
  PAID_OFFLINE: 'paid offline',
  PAST_DUE: 'past due',
  PROCESSING: 'processing',
  REQUESTED: 'requested',
  RETURNED: 'returned',
  SENT: 'sent',
};

const PAYMENT_STATUS_TYPE = {
  CAUTION: 'caution',
  INFO: 'info',
  IN_PROGRESS: 'in_progress',
  NEUTRAL: 'neutral',
  SUCCESS: 'success',
  WARN: 'warn',
} as const;

const PAYMENT_STATUS_TEXT_TO_TYPE = {
  [PAYMENT_STATUS_TEXT.FAILED]: PAYMENT_STATUS_TYPE.CAUTION,
  [PAYMENT_STATUS_TEXT.PAID]: PAYMENT_STATUS_TYPE.SUCCESS,
  [PAYMENT_STATUS_TEXT.PAID_OFFLINE]: PAYMENT_STATUS_TYPE.SUCCESS,
  [PAYMENT_STATUS_TEXT.PAST_DUE]: PAYMENT_STATUS_TYPE.CAUTION,
  [PAYMENT_STATUS_TEXT.PROCESSING]: PAYMENT_STATUS_TYPE.WARN,
  [PAYMENT_STATUS_TEXT.REQUESTED]: PAYMENT_STATUS_TYPE.NEUTRAL,
  [PAYMENT_STATUS_TEXT.RETURNED]: PAYMENT_STATUS_TYPE.CAUTION,
  [PAYMENT_STATUS_TEXT.SENT]: PAYMENT_STATUS_TYPE.WARN,
};

const PAYMENT_DESCRIPTION_TO_TYPE = {
  'monthly payment from': PAYMENT_TYPE.RECURRING_PAYMENT,
  'one-time payment from': PAYMENT_TYPE.ONE_TIME_PAYMENT,
} as Record<string, (typeof PAYMENT_TYPE)[keyof typeof PAYMENT_TYPE]>;

const PAYMENT_CONSTANTS = {
  CREDIT_CARD_FORM: {
    ID_SELECTOR: 'payment-form-iframe',
    CLASS_SELECTOR: 'payment-form',
    VERSION: 'v1',
  },
  PAYMENT_STATUS: {
    VALID: 'valid',
    INVALID: 'invalid',
    PENDING: 'pending',
  },
  PAYMENT_METHOD: {
    NEW: 'new',
    DEFAULT: 'default',
  },
  PAYMENT_PHASE: {
    SELECT_PAYMENT_METHOD: 'select payment method',
    CREDIT_CARD_CAPTURE: 'credit card capture',
    CREDIT_CARD_CHARGE: 'credit card charge',
    PUBLISH_LISTING: 'publish listing',
    CAUGHT_ERROR: 'caught error',
  },
  CAPTURE: {
    SCRIPT_COMMONS: 'https://www.zillowstatic.com/billing/payment_method/v1/overall-commons-31cd94799c.js',
    SCRIPT_APP: 'https://www.zillowstatic.com/billing/payment_method/v1/credit-card-iframe/app-50ac2b012a.js',
    INITIATE: {
      ERROR: {
        METROID_MAINTENANCE_MODE: 'METROID_MAINTENANCE_MODE',
        CANNOT_MAKE_PAYMENTS: 'CANNOT_MAKE_PAYMENTS',
        // Note: ZE-UI is a general error code, actual error is in code_details
        ZE_UI: 'ZE-UI',
      },
    },
  },
  CHARGE: {
    DEV: {
      SCRIPT_COMMONS: 'https://s.zillow.net/billing/billing_integration/v1/overall-commons-1b9c46851c.js',
      SCRIPT_APP: 'https://s.zillow.net/billing/billing_integration/v1/charge/app-79894e0846.js',
    },
    PROD: {
      SCRIPT_COMMONS: 'https://s.zillowstatic.com/billing/live/v1/overall-commons-1b9c46851c.js',
      SCRIPT_APP: 'https://s.zillowstatic.com/billing/live/v1/charge/app-79894e0846.js',
    },
    INITIATE: {
      ERROR: {
        TRY_AGAIN_LATER: 'TRY_AGAIN_LATER',
        CANNOT_MAKE_PAYMENTS: 'CANNOT_MAKE_PAYMENTS',
      },
    },
  },
  ERROR_TYPE: {
    // Form validation errors
    EXPIRATION_PAST: 'Expiration date must be a future date.',
    BAD_ZIP: 'Please enter a valid zip code.',
    INVALID_CARD_NUMBER: 'Invalid credit card number',

    // Payment processing errors
    PAYMENT_FAILURE: 'Transaction failed!',
    PAYMENT_PENDING: 'Your payment is pending. It may take up to 5 minutes to process. Please check back shortly.',

    // Service errors
    METROID_MAINTENANCE_MODE:
      "The payment service is currently down for maintenance. We'll be back up soon." + ' Thank you for your patience.',
    CANNOT_MAKE_PAYMENTS: 'You cannot make payments for this listing.',

    // Rate limiting and attempt errors
    MAX_ATTEMPTS_ERROR:
      'Transaction failed. You have had too many unsuccessful attempts, please check your information and try again later.',
    SUBMIT_TOO_QUICK: 'Too many attempts. Please wait a moment before trying again.',

    // Gateway transaction errors (payment processor declines)
    GATEWAY_TRANSACTION_ERROR:
      "We couldn't process your payment. Please check your card details or try a different card.",
    TRANSACTION_DECLINED: "We couldn't process your payment. Please check your card details or try a different card.",

    // Specific decline reasons
    CALL_VOICE_CENTER:
      "We couldn't add your payment method. Check that your card details are correct, or try a different card. If the issue continues, contact your bank.",
    CUSTOMER_NOT_AUTHORIZED: 'Please use a different payment method or contact support.',

    // Form validation errors
    HOSTED_PAGE_FIELD_VALIDATION_ERROR: 'Please check that all required fields are filled out correctly.',
    INVALID_VALUES: 'Some information you entered is invalid. Please review your payment details and try again.',
    INVALID_TIMEOUT: 'Your session timed out. Please refresh the page and try again.',
    RECAPTCHA_VALIDATION_FAILED: 'Security verification failed. Please refresh the page and try again.',

    // Default fallback
    DEFAULT: 'Unable to process payment. Please check your information and try again.',
  },
};

/**
 * Maps payment error to user-friendly message based on code and code_details
 * Similar to mapZuoraPaymentMethodError in zuoraUtils.ts and getMetroidErrorMessage in metroid.ts
 *
 * ZE-UI errors should be parsed based on the code_details field which follows the format:
 * [failure_reason] failure_message
 *
 * @param error - The error object with code, code_details, and optional message
 * @returns User-friendly error message
 */
export const mapPaymentError = (error: PaymentError): string => {
  if (!error) {
    return PAYMENT_CONSTANTS.ERROR_TYPE.DEFAULT;
  }

  const { code, code_details } = error;

  // Handle specific error codes first (non ZE-UI codes)
  if (code === PAYMENT_CONSTANTS.CAPTURE.INITIATE.ERROR.METROID_MAINTENANCE_MODE) {
    return PAYMENT_CONSTANTS.ERROR_TYPE.METROID_MAINTENANCE_MODE;
  }

  if (code === PAYMENT_CONSTANTS.CAPTURE.INITIATE.ERROR.CANNOT_MAKE_PAYMENTS) {
    return PAYMENT_CONSTANTS.ERROR_TYPE.CANNOT_MAKE_PAYMENTS;
  }

  // Handle ZE-UI errors by examining code_details
  // Check in order of frequency (most common first) based on Metroid documentation
  if (code === ERROR_CODES.ZE_UI && code_details) {
    // 1. GatewayTransactionError (most common) - payment gateway declines
    // Example: "[GatewayTransactionError] Transaction declined.89 - Credit Floor"
    if (ERROR_REGEX.GATEWAY_TRANSACTION_ERROR.test(code_details)) {
      const message = PAYMENT_CONSTANTS.ERROR_TYPE.GATEWAY_TRANSACTION_ERROR;
      return message;
    }

    // 2. Submit_Too_Quick - rate limiting
    if (ERROR_REGEX.SUBMIT_TOO_QUICK.test(code_details)) {
      const message = PAYMENT_CONSTANTS.ERROR_TYPE.SUBMIT_TOO_QUICK;
      return message;
    }

    // 3. HostedPageFieldValidationError - field validation errors
    if (ERROR_REGEX.HOSTED_PAGE_FIELD_VALIDATION_ERROR.test(code_details)) {
      const message = PAYMENT_CONSTANTS.ERROR_TYPE.HOSTED_PAGE_FIELD_VALIDATION_ERROR;
      return message;
    }

    // 4. Invalid_Values - invalid field values
    if (ERROR_REGEX.INVALID_VALUES.test(code_details)) {
      const message = PAYMENT_CONSTANTS.ERROR_TYPE.INVALID_VALUES;
      return message;
    }

    // 5. Invalid_TimeOut - timestamp validation failure
    if (ERROR_REGEX.INVALID_TIMEOUT.test(code_details)) {
      const message = PAYMENT_CONSTANTS.ERROR_TYPE.INVALID_TIMEOUT;
      return message;
    }

    // 6. Attempt_Exceed_Limitation - max attempts exceeded
    if (ERROR_REGEX.ATTEMPTS_EXCEEDED.test(code_details)) {
      const message = PAYMENT_CONSTANTS.ERROR_TYPE.MAX_ATTEMPTS_ERROR;
      return message;
    }

    // 7. ReCaptcha_Validation_Failed - reCAPTCHA failure
    if (ERROR_REGEX.RECAPTCHA_VALIDATION_FAILED.test(code_details)) {
      const message = PAYMENT_CONSTANTS.ERROR_TYPE.RECAPTCHA_VALIDATION_FAILED;
      return message;
    }

    // Legacy/specific patterns for backward compatibility
    // These handle cases where the bracketed format might not be present
    if (ERROR_REGEX.CUSTOMER_NOT_AUTHORIZED.test(code_details)) {
      const message = PAYMENT_CONSTANTS.ERROR_TYPE.CUSTOMER_NOT_AUTHORIZED;
      return message;
    }

    if (ERROR_REGEX.CALL_VOICE_CENTER.test(code_details)) {
      const message = PAYMENT_CONSTANTS.ERROR_TYPE.CALL_VOICE_CENTER;
      return message;
    }

    if (ERROR_REGEX.TRANSACTION_DECLINED.test(code_details)) {
      const message = PAYMENT_CONSTANTS.ERROR_TYPE.TRANSACTION_DECLINED;
      return message;
    }
  }

  // If error has a message property, use it
  if (error.message) {
    return error.message;
  }

  // Default error message
  const defaultMessage = PAYMENT_CONSTANTS.ERROR_TYPE.DEFAULT;
  return defaultMessage;
};

/**
 * Maps payment form error to user-friendly message based on code_details
 * Returns empty string if not a form-specific error that should be shown inline
 *
 * These are validation errors that should be displayed near the form fields
 * rather than as general error messages.
 *
 * @param error - The error object with code, code_details, and optional message
 * @returns Form-specific error message or empty string
 */
export const mapPaymentFormError = (error: PaymentError): string => {
  if (!error || error.code !== ERROR_CODES.ZE_UI || !error.code_details) {
    return '';
  }

  const { code_details } = error;

  // Form field validation errors that should be displayed inline
  // These are typically caught before submission but can also come from server-side validation

  if (ERROR_REGEX.EXPIRATION_PAST.test(code_details)) {
    const message = PAYMENT_CONSTANTS.ERROR_TYPE.EXPIRATION_PAST;
    return message;
  }

  if (ERROR_REGEX.ZIP_CODE.test(code_details)) {
    const message = PAYMENT_CONSTANTS.ERROR_TYPE.BAD_ZIP;
    return message;
  }

  if (ERROR_REGEX.INVALID_CARD_NUMBER.test(code_details)) {
    const message = PAYMENT_CONSTANTS.ERROR_TYPE.INVALID_CARD_NUMBER;
    return message;
  }

  // HostedPageFieldValidationError can contain specific field errors
  // Example: "[HostedPageFieldValidationError] creditCardHolderName:ExceededMaxLength"
  if (ERROR_REGEX.HOSTED_PAGE_FIELD_VALIDATION_ERROR.test(code_details)) {
    const message = PAYMENT_CONSTANTS.ERROR_TYPE.HOSTED_PAGE_FIELD_VALIDATION_ERROR;
    return message;
  }

  // If it's not a form-specific error, return empty string
  // This signals that the error should be handled as a general error
  return '';
};

export {
  PAYMENT_CONSTANTS,
  PAYMENT_DESCRIPTION_TO_TYPE,
  PAYMENT_STATUS_TEXT,
  PAYMENT_STATUS_TEXT_TO_TYPE,
  PAYMENT_STATUS_TYPE,
  PAYMENT_TYPE,
  ERROR_CODES,
  ERROR_REGEX,
};
